// src/modules/clinical-info/common/helpers.js
import { formatDate } from "../../../utils/formatters/date";

/**
 * Returns color styles based on days passed since discharge
 * Used for visual status indicators in discharged patients table
 * @param {number} daysPassed - Number of days since discharge
 * @returns {{color: string, iconColor: string}}
 */
export const getStatusStyles = (daysPassed) => {
  if (daysPassed <= 2) return { color: "#007A33", iconColor: "#00A650" }; // Green
  if (daysPassed <= 4) return { color: "#D1A000", iconColor: "#F1C40F" }; // Yellow
  if (daysPassed <= 6) return { color: "#B85000", iconColor: "#E67E22" }; // Orange
  return { color: "#A10000", iconColor: "#E74C3C" }; // Red
};

/**
 * Formats money values for display in tables
 * Handles various input formats and normalizes to USD format
 * @param {any} v - Value to format (can be string or number)
 * @returns {string} Formatted money string like "$ 1,234.56"
 */
export const formatMoneyCell = (v) => {
  if (v === null || v === undefined || v === "") return "";

  const original = String(v).trim();
  const hasSemicolon = /;\s*$/.test(original);

  // Basic cleanup
  let raw = original
    .replace(/\$/g, "")
    .replace(/USD/gi, "")
    .replace(/;/g, "")
    .replace(/\s+/g, "");

  // Detect decimal separator by the LAST "." or "," that appears
  const lastDot = raw.lastIndexOf(".");
  const lastComma = raw.lastIndexOf(",");
  const decimalSep =
    lastDot > lastComma ? (lastDot !== -1 ? "." : null) : (lastComma !== -1 ? "," : null);

  if (decimalSep) {
    const thousandSep = decimalSep === "." ? "," : ".";
    // Remove thousand separators and unify decimal to "."
    raw = raw
      .split(thousandSep).join("")
      .replace(decimalSep, ".");
  } else {
    // No decimal separator: remove any separators just in case
    raw = raw.replace(/[.,]/g, "");
  }

  const n = Number(raw);
  if (Number.isNaN(n)) return original;

  // USD format (en-US): 70,546.73
  const formatted = n.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  return `$ ${formatted}`;
};

/**
 * Formats a date string for table display (US standard: MM/DD/YYYY).
 * @param {string} dateStr - ISO date string
 * @returns {string} Formatted date or "N/A"
 */
export const formatDateForTable = (dateStr) => formatDate(dateStr, "N/A");
